import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
  Label,
} from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import type { Category } from '../../features/categories/types';
import { fetchCategories } from '../../features/categories/categories.action';
import { useCategoriesStore } from '../../features/categories/categories.store';
import { useDiagramStore } from '@diagram/ui/hooks/diagram-store.hook';

const FilterComboBox = memo(() => {
  const [query, setQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(
    null
  );
  const categories = useCategoriesStore((state) => state.items);
  const activeCategory = useDiagramStore((state) => state.activeCategory);

  const setActiveCategory = useDiagramStore(
    (state) => state.drilldown.setActiveCategory
  );
  const allOption = {
    id: '',
    name: 'All',
    color: '#000000',
    ownerId: '',
  };
  useEffect(
    () => {
      fetchCategories();
      if (activeCategory) {
        setSelectedCategory(categories[activeCategory]);
      } else {
        setSelectedCategory(allOption);
      }
    },
    // eslint-disable-next-line
    []
  );
  const handleInputChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      e.stopPropagation();
      setQuery(e.target.value);
    },
    []
  );

  const handleSelection = useCallback(
    (category: Category | null) => {
      if (category) {
        setActiveCategory(category.id);
        setSelectedCategory(category);
      } else {
        setActiveCategory('');
        setSelectedCategory(null);
      }
    },
    [setActiveCategory]
  );

  const filteredCategories = useMemo(
    () =>
      Object.values(categories).filter((category) =>
        category.name.toLowerCase().includes(query.toLowerCase())
      ),
    [categories, query]
  );

  return (
    <Combobox as="div" value={selectedCategory} onChange={handleSelection}>
      <Label className="text-sm leading-8 text-gray-800">
        DrillDown Category
      </Label>
      <div className="relative mt-2">
        <ComboboxInput
          className="block w-full rounded-md bg-white py-1.5 pr-12 pl-8 text-sm text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-primary sm:text-sm/6"
          onChange={handleInputChange}
          onBlur={() => setQuery('')}
          displayValue={(category: Category) => category?.name}
        />
        {selectedCategory && (
          <div
            className="absolute left-3 top-1/2 size-2 rounded-full -translate-y-1/2"
            style={{ backgroundColor: selectedCategory.color }}
          />
        )}
        <ComboboxButton className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-hidden">
          <ChevronUpDownIcon
            className="size-5 text-gray-400"
            aria-hidden="true"
          />
        </ComboboxButton>
        <ComboboxOptions className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base ring-1 shadow-lg ring-black/5 focus:outline-hidden sm:text-sm">
          <ComboboxOption
            value={allOption}
            className="group relative cursor-default py-2 pr-9 pl-3 text-gray-900 select-none data-focus:bg-primary data-focus:text-white data-focus:outline-hidden"
          >
            <span className="block truncate group-data-selected:font-semibold">
              All
            </span>
            <span className="absolute inset-y-0 right-0 hidden items-center pr-4 text-primary group-data-focus:text-white group-data-selected:flex">
              <CheckIcon className="size-5" aria-hidden="true" />
            </span>
          </ComboboxOption>
          {filteredCategories.length > 0 &&
            filteredCategories.map((category) => (
              <ComboboxOption
                key={category.id}
                value={category}
                className="group relative cursor-default py-2 pr-9 pl-3 text-gray-900 select-none data-focus:bg-indigo-600 data-focus:text-white data-focus:outline-hidden"
              >
                <div className="flex items-center gap-2">
                  <div
                    className="size-2 rounded-full bg-gray-300"
                    style={{ backgroundColor: category.color }}
                  ></div>
                  <span className="block truncate group-data-selected:font-semibold">
                    {category.name}
                  </span>
                </div>

                <span className="absolute inset-y-0 right-0 hidden items-center pr-4 text-primary group-data-focus:text-white group-data-selected:flex">
                  <CheckIcon className="size-5" aria-hidden="true" />
                </span>
              </ComboboxOption>
            ))}
        </ComboboxOptions>
      </div>
    </Combobox>
  );
});
export default FilterComboBox;
